import React, { useEffect, useRef, useState } from 'react';
import { fetchAddressPlaceDetails, fetchAddressSuggestions } from './restaurantApi';
import './css/AddressTable.css';

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

const displayValue = (value) => {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
};

const DetailField = ({ label, value, wide = false }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className="restaurant-detail-label">{label}</span>
    <span className="restaurant-detail-value">{displayValue(value)}</span>
  </div>
);

const AddressTable = ({ addresses, onUpdate, readOnly = false, sectionTitle = null }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedAddress, setEditedAddress] = useState({});
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const addressSearchTimeoutRef = useRef(null);

  const clearAddressSearch = () => {
    setAddressSearch('');
    setAddressSuggestions([]);
    setAddressSearchLoading(false);
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (addressSearchTimeoutRef.current) {
        clearTimeout(addressSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditedAddress({ ...addresses[index] });
    clearAddressSearch();
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedAddress({});
    clearAddressSearch();
  };

  const handleChange = (field, value) => {
    setEditedAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const postal = String(editedAddress.postalCode ?? '').trim();
    if (!postal) {
      alert('Postal code is required');
      return;
    }
    if (!/^\d+$/.test(postal)) {
      alert('Postal code must be a valid number');
      return;
    }
    const lat = parseFloat(editedAddress.lat ?? editedAddress.latitude ?? '');
    const lon = parseFloat(editedAddress.lon ?? editedAddress.longitude ?? '');
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      alert('Latitude and longitude are required. Search and select an address to set them.');
      return;
    }
    onUpdate(editingIndex, {
      ...editedAddress,
      postalCode: postal,
      lat: String(lat),
      lon: String(lon),
    });
    setEditingIndex(null);
    setEditedAddress({});
    clearAddressSearch();
  };

  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    setAddressSearch(value);
    setAddressSuggestions([]);

    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }

    if (value.trim().length < 2) {
      setAddressSearchLoading(false);
      return;
    }

    addressSearchTimeoutRef.current = setTimeout(async () => {
      addressSearchTimeoutRef.current = null;
      try {
        setAddressSearchLoading(true);
        const list = await fetchAddressSuggestions(value.trim());
        setAddressSuggestions(list);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setAddressSuggestions([]);
      } finally {
        setAddressSearchLoading(false);
      }
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
  };

  const handleSuggestionSelect = async (placeId) => {
    if (!placeId) return;

    try {
      setAddressSearchLoading(true);
      const details = await fetchAddressPlaceDetails(placeId);
      if (!details) {
        alert('Address details not found');
        return;
      }

      setEditedAddress((prev) => ({
        ...prev,
        addressLine1: details.addressLine1 ?? '',
        addressLine2: (details.addressLine2 ?? '').trim(),
        city: details.city ?? '',
        state: details.state ?? '',
        country: details.country ?? '',
        postalCode: details.postalCode ?? '',
        lat:
          details.latitude != null
            ? String(details.latitude)
            : details.lat != null
              ? String(details.lat)
              : prev.lat ?? '',
        lon:
          details.longitude != null
            ? String(details.longitude)
            : details.lon != null
              ? String(details.lon)
              : prev.lon ?? '',
        areaName: prev.areaName || details.city || '',
      }));
      setAddressSuggestions([]);
      setAddressSearch('');
    } catch (error) {
      console.error('Error fetching place details:', error);
      alert(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to load address details'
      );
    } finally {
      setAddressSearchLoading(false);
    }
  };

  if (!addresses || addresses.length === 0) {
    return <p className="restaurant-detail-message">No addresses available.</p>;
  }

  return (
    <div className="restaurant-detail-address-list">
      {sectionTitle && addresses.length > 1 ? (
        <div className="restaurant-detail-card-header restaurant-detail-address-section-header">
          <h3 className="restaurant-detail-section-title">{sectionTitle}</h3>
        </div>
      ) : null}
      {addresses.map((address, index) => {
        const isEditing = editingIndex === index;
        const showNumberedTitle = addresses.length > 1;
        const useSectionTitle = Boolean(sectionTitle) && addresses.length === 1;
        const blockTitle = showNumberedTitle ? `Address ${index + 1}` : null;
        const showHeader = useSectionTitle || blockTitle || !readOnly;

        return (
          <div
            key={address.addressId ?? index}
            className={`restaurant-detail-address-block${
              isEditing ? ' restaurant-detail-address-block--editing' : ''
            }`}
          >
            {showHeader ? (
              <div
                className={`restaurant-detail-card-header restaurant-detail-address-block-header${
                  useSectionTitle ? ' restaurant-detail-address-section-header' : ''
                }`}
              >
                {useSectionTitle ? (
                  <h3 className="restaurant-detail-section-title">{sectionTitle}</h3>
                ) : blockTitle ? (
                  <h4 className="restaurant-detail-address-block-title">{blockTitle}</h4>
                ) : (
                  <span />
                )}
                {!readOnly ? (
                  <div className="restaurant-detail-card-header-actions">
                    {isEditing ? (
                      <div className="restaurant-detail-edit-actions">
                        <button
                          type="button"
                          className="eats-admin-header-btn"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="eats-admin-header-btn"
                          onClick={handleSave}
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="eats-admin-header-btn"
                        onClick={() => handleEdit(index)}
                        disabled={editingIndex != null && editingIndex !== index}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {isEditing ? (
              <div className="restaurant-detail-edit-form restaurant-detail-address-edit">
                <div className="address-edit-search-wrap">
                  <label className="address-edit-search-label" htmlFor={`address-search-${index}`}>
                    Search address
                  </label>
                  <input
                    id={`address-search-${index}`}
                    type="text"
                    className="address-edit-search-input"
                    value={addressSearch}
                    onChange={handleAddressSearchChange}
                    placeholder="Type to search (e.g. Hyderabad)..."
                    autoComplete="off"
                  />
                  {addressSearchLoading ? (
                    <span className="address-edit-search-loading">Searching…</span>
                  ) : null}
                  {addressSuggestions.length > 0 ? (
                    <ul className="address-suggestions-dropdown">
                      {addressSuggestions.map((suggestion, suggestionIndex) => {
                        const placeId = suggestion.place_id ?? suggestion.placeId;
                        const text =
                          suggestion.description ?? suggestion.formatted_address ?? '';
                        return (
                          <li
                            key={placeId || suggestionIndex}
                            onClick={() => handleSuggestionSelect(placeId)}
                          >
                            {text}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>

                <div className="get-restaurant-form-row flex">
                  <div className="get-restaurant-form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      value={editedAddress.addressLine1 ?? ''}
                      onChange={(e) => handleChange('addressLine1', e.target.value)}
                    />
                  </div>
                  <div className="get-restaurant-form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      value={editedAddress.addressLine2 ?? ''}
                      onChange={(e) => handleChange('addressLine2', e.target.value)}
                    />
                  </div>
                  <div className="get-restaurant-form-group">
                    <label>Area</label>
                    <input
                      type="text"
                      value={editedAddress.areaName ?? ''}
                      onChange={(e) => handleChange('areaName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="get-restaurant-form-row flex">
                  <div className="get-restaurant-form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={editedAddress.city ?? ''}
                      onChange={(e) => handleChange('city', e.target.value)}
                    />
                  </div>
                  <div className="get-restaurant-form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={editedAddress.state ?? ''}
                      onChange={(e) => handleChange('state', e.target.value)}
                    />
                  </div>
                  <div className="get-restaurant-form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={editedAddress.country ?? ''}
                      onChange={(e) => handleChange('country', e.target.value)}
                    />
                  </div>
                </div>

                <div className="get-restaurant-form-row flex">
                  <div className="get-restaurant-form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      value={editedAddress.postalCode ?? ''}
                      onChange={(e) => handleChange('postalCode', e.target.value)}
                      required
                      placeholder="Postal code *"
                    />
                  </div>
                  <div className="get-restaurant-form-group">
                    <label>Home Delivery Available</label>
                    <select
                      value={editedAddress.homeDeliveryAvailable ? 'yes' : 'no'}
                      onChange={(e) =>
                        handleChange('homeDeliveryAvailable', e.target.value === 'yes')
                      }
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="restaurant-detail-grid">
                <DetailField label="Address Line 1" value={address.addressLine1} />
                <DetailField label="Address Line 2" value={address.addressLine2} />
                <DetailField label="Area" value={address.areaName} />
                <DetailField label="City" value={address.city} />
                <DetailField label="State" value={address.state} />
                <DetailField label="Country" value={address.country} />
                <DetailField label="Postal Code" value={address.postalCode} />
                <DetailField
                  label="Home Delivery Available"
                  value={address.homeDeliveryAvailable ? 'Yes' : 'No'}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AddressTable;
